import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";

const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Builder = lazy(() => import("./pages/Builder"));
const PublicResume = lazy(() => import("./pages/PublicResume"));
const CoverLetter = lazy(() => import("./pages/CoverLetter"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResourcesHub = lazy(() => import("./pages/resources/ResourcesHub"));
const ResourceArticle = lazy(() => import("./pages/resources/ResourceArticle"));
const Features = lazy(() => import("./pages/Features"));
const Templates = lazy(() => import("./pages/Templates"));
const LandingPageRoute = lazy(() => import("./pages/LandingPageRoute"));
const GuidesHub = lazy(() => import("./pages/guides/GuidesHub"));
const GuideArticle = lazy(() => import("./pages/guides/GuideArticle"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Press = lazy(() => import("./pages/Press"));
const SitemapHtml = lazy(() => import("./pages/SitemapHtml"));
const ResumeAnalyzer = lazy(() => import("./pages/ResumeAnalyzer"));
const ResumeProofreader = lazy(() => import("./pages/ResumeProofreader"));
const SalaryNegotiation = lazy(() => import("./pages/SalaryNegotiation"));
const InterviewPreparation = lazy(() => import("./pages/InterviewPreparation"));
const CoverLetterGenerator = lazy(() => import("./pages/CoverLetterGenerator"));
import { landingPages } from "@/data/landingPages";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    {/* Light is the product's default look. enableSystem={false} stops a
        visitor whose OS is in dark mode from landing on the dark theme before
        they have chosen it; the header toggle still switches and persists. */}
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/builder/:id" element={<ProtectedRoute><Builder /></ProtectedRoute>} />
              <Route path="/cover-letter/:resumeId" element={<ProtectedRoute><CoverLetter /></ProtectedRoute>} />
              <Route path="/r/:slug" element={<PublicResume />} />
              <Route path="/resources" element={<ResourcesHub />} />
              <Route path="/resources/:slug" element={<ResourceArticle />} />
              <Route path="/features" element={<Features />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/guides" element={<GuidesHub />} />
              <Route path="/guides/:slug" element={<GuideArticle />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/press" element={<Press />} />
              <Route path="/resume-analyzer" element={<ResumeAnalyzer />} />
              <Route path="/resume-proofreader" element={<ResumeProofreader />} />
              <Route path="/salary-negotiation" element={<SalaryNegotiation />} />
              <Route path="/interview-preparation" element={<InterviewPreparation />} />
              <Route path="/cover-letter-generator" element={<CoverLetterGenerator />} />
              <Route path="/sitemap-html" element={<SitemapHtml />} />
              {landingPages.map((p) => (
                <Route
                  key={p.slug}
                  path={p.slug}
                  element={<LandingPageRoute page={p} />}
                />
              ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
